import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Modal, Form, Table, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

// Services
import { agentService } from '../services/agentService';

const MyCryptoWalletPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [wallets, setWallets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [selectedQR, setSelectedQR] = useState(null);
    const [editingWallet, setEditingWallet] = useState(null);
    const fileInputRef = useRef(null);

    // Form State
    const [formData, setFormData] = useState({
        walletAddress: '',
        network: '',
        qrCodeImage: ''
    });

    const [qrPreview, setQrPreview] = useState(null);

    const networkOptions = [
        { value: 'USDC', label: 'USDC' },
        { value: 'Binance Pay', label: 'Binance Pay' },
        { value: 'TRC20', label: 'TRC20' },
        { value: 'BEP20', label: 'BEP20' },
        { value: 'BTC', label: 'BTC' },
        { value: 'ETH', label: 'ETH' },
        { value: 'BNB', label: 'BNB' }
    ];

    const fetchWallets = useCallback(async () => {
        try {
            const data = await agentService.getMyWallets();
            if (data.success) {
                setWallets(data.cryptoWallets);
            }
        } catch (error) {
            toast.error(t('error_history_fetch', 'Failed to fetch crypto wallets'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchWallets();
    }, [fetchWallets]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                toast.error(t('image_too_large', 'Image must be less than 2MB'));
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                setQrPreview(reader.result);
                setFormData(prev => ({ ...prev, qrCodeImage: reader.result }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = () => {
        setQrPreview(null);
        setFormData(prev => ({ ...prev, qrCodeImage: '' }));
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleEdit = (wallet) => {
        setEditingWallet(wallet);
        setFormData({
            walletAddress: wallet.walletAddress,
            network: wallet.network,
            qrCodeImage: wallet.qrCodeImage || ''
        });
        setQrPreview(wallet.qrCodeImage || null);
        setShowModal(true);
    };

    const handleAdd = () => {
        setEditingWallet(null);
        setFormData({
            walletAddress: '',
            network: '',
            qrCodeImage: ''
        });
        setQrPreview(null);
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitLoading(true);
        try {
            let response;
            if (editingWallet) {
                response = await agentService.updateMyCryptoWallet(editingWallet.id, formData);
            } else {
                response = await agentService.addMyCryptoWallet(formData);
            }

            if (response.success) {
                toast.success(editingWallet ? t('msg_update_success', 'Updated successfully') : t('msg_save_success', 'Crypto wallet added successfully'));
                setShowModal(false);
                setFormData({ walletAddress: '', network: '', qrCodeImage: '' });
                setQrPreview(null);
                setEditingWallet(null);
                fetchWallets();
            }
        } catch (error) {
            toast.error(t(error.message || 'error_process_request'));
        } finally {
            setSubmitLoading(false);
        }
    };


    return (
        <div className="majestic-page-wrapper p-4">
            <div className="d-flex align-items-center mb-4 pb-3" style={{ borderBottom: '1px solid rgba(212, 175, 55, 0.15)' }}>
                <Button variant="link" className="p-0 text-white me-3" onClick={() => navigate('/my-wallet')}>
                    <i className="bi bi-arrow-left fs-4"></i>
                </Button>
                <div>
                    <h4 className="mb-0 text-white fw-bold">{t('my_crypto_wallets')}</h4>
                    <small className="text-secondary">{t('manage_crypto_wallets', 'Manage your linked crypto wallets')}</small>
                </div>
                <Button variant="warning" className="ms-auto rounded-pill px-4 fw-bold shadow-sm" onClick={handleAdd}>
                    <i className="bi bi-plus-lg me-2"></i>{t('add_wallet')}
                </Button>
            </div>

            {loading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="warning" /></div>
            ) : wallets.length === 0 ? (
                <div className="text-center py-5" style={{ background: 'rgba(255,255,255,0.02)', borderRadius: '12px', border: '1px dashed rgba(255,255,255,0.1)' }}>
                    <i className="bi bi-wallet2 text-secondary opacity-50" style={{ fontSize: '48px' }}></i>
                    <div className="text-secondary mt-3">{t('no_crypto_wallets_linked', 'No crypto wallets linked yet.')}</div>
                </div>
            ) : (
                <div className="majestic-table-wrapper rounded-3" style={{ border: '1px solid rgba(212, 175, 55, 0.15)', background: 'linear-gradient(145deg, rgba(26,26,26,0.9), rgba(13,13,13,0.95))', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <Table hover variant="dark" className="mb-0 align-middle bg-transparent">
                        <thead style={{ background: 'rgba(0,0,0,0.4)' }}>
                            <tr style={{ borderBottom: '1px solid rgba(212, 175, 55, 0.2)' }}>
                                <th className="py-3 ps-4 text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '1.5px', color: '#D4AF37', width: '160px' }}>
                                    <i className="bi bi-hdd-network me-2 opacity-50"></i>{t('network')}
                                </th>
                                <th className="py-3 text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '1.5px', color: '#D4AF37', paddingLeft: '40px', width: '45%' }}>
                                    <i className="bi bi-wallet2 me-2 opacity-50"></i>{t('wallet_address')}
                                </th>
                                <th className="py-3 text-uppercase fw-bold text-center" style={{ fontSize: '0.7rem', letterSpacing: '1.5px', color: '#D4AF37', width: '220px' }}>
                                    <i className="bi bi-qr-code me-2 opacity-50"></i>{t('wallet_qr_code', 'QR Code')}
                                </th>
                                <th className="py-3 text-uppercase fw-bold text-end pe-4" style={{ fontSize: '0.7rem', letterSpacing: '1.5px', color: '#D4AF37', width: '130px' }}>
                                    {t('actions', 'Actions')}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {wallets.map((wallet, idx) => (
                                <tr key={wallet.id} style={{ borderBottom: idx < wallets.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none', transition: 'background 0.2s' }}>
                                    <td className="ps-4 py-3">
                                        <span className="badge rounded-pill px-3 py-2" style={{ background: 'rgba(212, 175, 55, 0.15)', color: '#D4AF37', fontWeight: '600', minWidth: '100px', textAlign: 'center' }}>{wallet.network}</span>
                                    </td>
                                    <td className="py-3 font-monospace text-break" style={{ letterSpacing: '0.5px', color: 'rgba(255,255,255,0.7)', paddingLeft: '40px' }}>{wallet.walletAddress}</td>
                                    <td className="py-3 text-center">
                                        {wallet.qrCodeImage ? (
                                            <div
                                                className="d-inline-block bg-white p-1 rounded-2 cursor-pointer"
                                                style={{ transition: 'transform 0.2s' }}
                                                onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                                                onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                                onClick={() => setSelectedQR(wallet.qrCodeImage)}
                                            >
                                                <img src={wallet.qrCodeImage} alt="QR" style={{ width: '45px', height: '45px', objectFit: 'contain' }} />
                                            </div>
                                        ) : (
                                            <span className="text-secondary opacity-50">-</span>
                                        )}
                                    </td>
                                    <td className="py-3 text-end pe-4">
                                        <Button
                                            variant="outline-warning"
                                            size="sm"
                                            className="rounded-pill px-3"
                                            onClick={() => handleEdit(wallet)}
                                            style={{ fontSize: '0.75rem' }}
                                        >
                                            <i className="bi bi-pencil-square me-1"></i>{t('edit', 'Edit')}
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            )}

            {/* Add/Edit Modal */}
            <Modal show={showModal} onHide={() => { setShowModal(false); setEditingWallet(null); }} centered contentClassName="majestic-card border-0">
                <Modal.Header closeButton className="border-bottom border-secondary border-opacity-10 py-3">
                    <Modal.Title className="fw-bold text-white fs-5">{editingWallet ? t('edit_crypto_wallet', 'Edit Crypto Wallet') : t('add_crypto_wallet')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body className="p-4">
                        {/* Network */}
                        <div className="majestic-form-group mb-3">
                            <div className="majestic-label-row">
                                <span className="majestic-label-dot"></span>
                                <label htmlFor="network" className="majestic-label-text">
                                    {t('network')}
                                    <span style={{ color: '#ff4d4d', marginLeft: '4px' }}>*</span>
                                </label>
                            </div>
                            <div className="majestic-input-wrapper">
                                <Form.Select
                                    id="network"
                                    name="network"
                                    className="majestic-input-field"
                                    value={formData.network}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">{t('select_network', 'Select Network')}</option>
                                    {networkOptions.map(opt => (
                                        <option key={opt.value} value={opt.value} className="bg-dark text-white">{opt.label}</option>
                                    ))}
                                </Form.Select>
                            </div>
                        </div>

                        {/* Wallet Address */}
                        <div className="majestic-form-group mb-3">
                            <div className="majestic-label-row">
                                <span className="majestic-label-dot"></span>
                                <label htmlFor="walletAddress" className="majestic-label-text">
                                    {t('wallet_address')}
                                    <span style={{ color: '#ff4d4d', marginLeft: '4px' }}>*</span>
                                </label>
                            </div>
                            <div className="majestic-input-wrapper">
                                <Form.Control
                                    id="walletAddress"
                                    name="walletAddress"
                                    className="majestic-input-field"
                                    value={formData.walletAddress}
                                    onChange={handleInputChange}
                                    required
                                    placeholder={t('enter_wallet_address')}
                                />
                            </div>
                        </div>

                        {/* QR Code Upload */}
                        <div className="majestic-form-group mb-3">
                            <div className="majestic-label-row">
                                <span className="majestic-label-dot"></span>
                                <span className="majestic-label-text">{t('wallet_qr_code', 'Wallet Address QR Code')}</span>
                            </div>
                            <div
                                className="d-flex flex-column align-items-center justify-content-center p-3 rounded-3"
                                style={{
                                    background: 'rgba(255,255,255,0.03)',
                                    border: '2px dashed rgba(255,255,255,0.15)',
                                    minHeight: '160px',
                                    cursor: 'pointer'
                                }}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                {qrPreview ? (
                                    <div className="position-relative">
                                        <img
                                            src={qrPreview}
                                            alt="QR Code Preview"
                                            style={{ width: '120px', height: '120px', objectFit: 'contain', borderRadius: '8px' }}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-danger position-absolute top-0 end-0"
                                            style={{ transform: 'translate(50%, -50%)', borderRadius: '50%', padding: '2px 6px' }}
                                            onClick={(e) => { e.stopPropagation(); handleRemoveImage(); }}
                                        >
                                            <i className="bi bi-x"></i>
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <i className="bi bi-qr-code text-secondary" style={{ fontSize: '32px' }}></i>
                                        <div className="text-secondary small mt-2">{t('click_upload_qr', 'Click to upload QR Code')}</div>
                                        <div className="text-secondary" style={{ fontSize: '10px' }}>{t('max_size', 'Max 2MB')}</div>
                                    </>
                                )}
                                <input
                                    id="qrCodeUpload"
                                    name="qrCodeUpload"
                                    type="file"
                                    ref={fileInputRef}
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    onChange={handleImageUpload}
                                />
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="border-top border-secondary border-opacity-10">
                        <Button variant="link" className="text-secondary text-decoration-none" onClick={() => { setShowModal(false); setEditingWallet(null); }}>{t('cancel')}</Button>
                        <Button variant="warning" type="submit" className="px-4 fw-bold" disabled={submitLoading} style={{ borderRadius: '12px' }}>
                            {submitLoading ? t('saving') : (editingWallet ? t('save_changes', 'Save Changes') : t('add_wallet'))}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* QR Large View Modal - Moved outside constraints to allow perfect viewport centering */}
            {selectedQR && (
                <div
                    className="qr-modal-overlay"
                    onClick={() => setSelectedQR(null)}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.85)',
                        backdropFilter: 'blur(8px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                        animation: 'fadeIn 0.3s ease'
                    }}
                >
                    <div
                        className="qr-modal-content"
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: 'linear-gradient(145deg, #1a1a1a, #0d0d0d)',
                            padding: '25px',
                            borderRadius: '24px',
                            border: '1px solid rgba(212, 175, 55, 0.3)',
                            boxShadow: '0 0 50px rgba(212, 175, 55, 0.2)',
                            maxWidth: '90%',
                            width: '320px',
                            maxHeight: '90vh',
                            overflowY: 'auto',
                            textAlign: 'center',
                            position: 'relative'
                        }}
                    >
                        <div
                            style={{
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                color: 'white',
                                cursor: 'pointer',
                                fontSize: '24px',
                                zIndex: 10
                            }}
                            onClick={() => setSelectedQR(null)}
                        >
                            <i className="bi bi-x-circle-fill opacity-75"></i>
                        </div>

                        <div className="mb-3">
                            <i className="bi bi-qr-code text-warning fs-3"></i>
                            <h5 className="text-white mt-2 fw-bold">{t('wallet_qr_code', 'Wallet Address QR Code')}</h5>
                        </div>

                        <div
                            style={{
                                background: '#fff',
                                padding: '15px',
                                borderRadius: '16px',
                                boxShadow: '0 5px 15px rgba(0,0,0,0.5)'
                            }}
                        >
                            <img
                                src={selectedQR}
                                alt="QR Large"
                                style={{
                                    width: '100%',
                                    maxHeight: '50vh',
                                    height: 'auto',
                                    display: 'block',
                                    borderRadius: '8px',
                                    objectFit: 'contain'
                                }}
                            />
                        </div>

                        <button
                            className="btn btn-warning w-100 mt-4 py-2 fw-bold"
                            style={{ borderRadius: '12px', background: '#D4AF37', border: 'none', color: '#000' }}
                            onClick={() => setSelectedQR(null)}
                        >
                            {t('close', 'Close')}
                        </button>
                    </div>
                </div>
            )}

            <style>
                {`
                @keyframes fadeIn {
                    from { opacity: 0; transform: scale(0.95); }
                    to { opacity: 1; transform: scale(1); }
                }
                .qr-modal-content {
                    animation: slideUpModal 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                }
                @keyframes slideUpModal {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                `}
            </style>
        </div >
    );
};

export default MyCryptoWalletPage;

